import React from "react";
import { Timer, PauseCircle, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import RelativeTime from "components/RelativeTime";
import {
  computeTicketSla,
  formatCountdown,
  formatTargetMins,
  SLA_STATE,
  SLA_TONE,
} from "utilities/sla";

/**
 * SlaPanel (SOUND-534) — ticket workspace right-rail SLA block. Shows the
 * resolved policy, both clocks (first-response + resolution) with target,
 * due date, and elapsed-vs-due state, first-response status, and the paused
 * affordance. Internal-only (v1 has no client-facing SLA countdowns) — gate at
 * the call site. Reads the backend-stamped `ticket.sla`; the color/countdown is
 * derived display only (utilities/sla).
 */
export default function SlaPanel({ ticket, policyName, now, className = "" }) {
  const sla = computeTicketSla(ticket, now);
  if (!sla.hasSla) return null;
  const raw = ticket.sla || {};

  return (
    <section className={`surface p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Timer className="h-3.5 w-3.5" />
          SLA
        </h3>
        {sla.paused && (
          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            <PauseCircle className="h-3.5 w-3.5" />
            Paused
          </span>
        )}
      </div>

      {(policyName || sla.hasSla) && (
        <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/60 pb-2.5">
          <span className="min-w-0 truncate text-sm font-medium text-foreground">
            {policyName || "SLA policy"}
          </span>
          <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
            24×7
          </span>
        </div>
      )}

      {sla.paused && (
        <p className="mb-3 rounded-md border border-border bg-muted/40 px-2.5 py-2 text-xs text-muted-foreground">
          Clocks are paused while the ticket is waiting — countdowns resume when it moves back into progress.
        </p>
      )}

      <div className="space-y-3">
        <ClockRow
          label="First response"
          clock={sla.firstResponse}
          respondedAt={raw.firstRespondedAt}
          targetMins={raw.firstResponseTargetMins}
          paused={sla.paused}
        />
        <ClockRow
          label="Resolution"
          clock={sla.resolution}
          targetMins={raw.resolutionTargetMins}
          paused={sla.paused}
        />
      </div>
    </section>
  );
}

function ClockRow({ label, clock, targetMins, respondedAt, paused }) {
  // Clock not configured for this target.
  if (targetMins == null) return null;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <ClockState clock={clock} respondedAt={respondedAt} paused={paused} />
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        Target {formatTargetMins(targetMins)}
        {clock?.dueAt && (
          <>
            {" · "}
            {clock.done ? "was due " : "due "}
            <RelativeTime value={clock.dueAt} />
          </>
        )}
      </div>
    </div>
  );
}

function ClockState({ clock, respondedAt, paused }) {
  // First-response satisfied.
  if (respondedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-success" title="Responded">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Responded <RelativeTime value={respondedAt} />
      </span>
    );
  }
  // Resolution satisfied (ticket closed) or otherwise met.
  if (clock?.done || clock?.state === SLA_STATE.MET) {
    const tone = SLA_TONE.met;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone.text}`} title="Met">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Met
      </span>
    );
  }
  if (!clock) return <span className="text-xs text-muted-foreground">—</span>;

  const countdown = formatCountdown(clock.remainingMs);

  if (paused) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground" title="Clock frozen while paused">
        <PauseCircle className="h-3.5 w-3.5" />
        {countdown} <span className="text-muted-foreground/70">(paused)</span>
      </span>
    );
  }

  if (clock.state === SLA_STATE.OK) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {countdown}
      </span>
    );
  }

  const tone = SLA_TONE[clock.state] || SLA_TONE.ok;
  const Icon = clock.state === SLA_STATE.BREACHED ? AlertTriangle : Clock;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone.text}`}>
      <Icon className="h-3.5 w-3.5" />
      {countdown}
    </span>
  );
}
